import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiService';

const DSRReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [filters, setFilters] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString().split('T')[0];
      }

      const response = await api.get('/reports/dsr', { params });
      
      console.log('=== DSR REPORT DEBUG ===');
      console.log('1. Full API Response:', response);
      console.log('2. Response Data:', response.data);
      console.log('3. Response Data Success:', response.data?.success);
      
      // The API response structure is: response.data.success and response.data.data
      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;
        
        console.log('4. Raw Invoices from API:', apiData.invoices);
        console.log('5. Invoices Array Length:', apiData.invoices?.length);
        
        // Add unique ID to each invoice for DataGrid
        const invoicesWithId = (apiData.invoices || []).map((invoice, index) => ({
          ...invoice,
          id: invoice.invoiceNo || index, // Use invoiceNo as ID, fallback to index
        }));
        
        console.log('6. Invoices with ID added:', invoicesWithId);
        console.log('7. Invoices with ID Length:', invoicesWithId.length);
        
        // Set the invoices data for the grid
        setReportData(invoicesWithId);
        setTotals(apiData.totals || null);
        
        console.log('8. State Updated - reportData should be:', invoicesWithId);
        console.log('9. State Updated - totals should be:', apiData.totals);
        
        // Log all data sections
        console.log('10. Daily Summary:', apiData.dailySummary);
        console.log('11. Hourly Breakdown:', apiData.hourlyBreakdown);
        console.log('12. Payment Status:', apiData.paymentStatus);
        console.log('13. Top Customers:', apiData.topCustomers);
      } else {
        console.error('API Response failed or no data:', response);
      }
    } catch (err) {
      console.error('Error fetching DSR report:', err);
      alert('Failed to fetch DSR report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  // Define columns for the data grid
  const columns = [
    { 
      field: 'invoiceNo', 
      headerName: 'Invoice No', 
      flex: 1,
      minWidth: 130
    },
    { 
      field: 'invoiceDate', 
      headerName: 'Date & Time', 
      flex: 1.2,
      minWidth: 150,
      valueFormatter: ({ value }) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    { 
      field: 'customerName', 
      headerName: 'Customer', 
      flex: 1.5,
      minWidth: 180
    },
    { 
      field: 'customerType', 
      headerName: 'Type', 
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          size="small"
          color={params.value === 'B2B' ? 'primary' : 'secondary'}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    { 
      field: 'subTotal', 
      headerName: 'Sub Total', 
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
    },
    { 
      field: 'vatAmt', 
      headerName: 'VAT', 
      flex: 0.8,
      minWidth: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
    },
    { 
      field: 'totalAmt', 
      headerName: 'Total', 
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      cellClassName: 'font-weight-bold'
    },
    { 
      field: 'paidAmt', 
      headerName: 'Paid', 
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
    },
    { 
      field: 'balanceAmt', 
      headerName: 'Balance', 
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      cellClassName: (params) => params.value > 0 ? 'text-error' : ''
    },
    { 
      field: 'paymentStatus', 
      headerName: 'Status', 
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <Chip 
          label={params.value?.toUpperCase() || 'N/A'}
          size="small"
          color={params.value === 'paid' ? 'success' : params.value === 'pending' ? 'warning' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      )
    },
  ];

  // Debug logs for render
  console.log('=== RENDER DEBUG ===');
  console.log('14. Current reportData state:', reportData);
  console.log('15. reportData length:', reportData.length);
  console.log('16. Current totals state:', totals);
  console.log('17. Current loading state:', loading);
  console.log('18. Columns defined:', columns);

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {/* Header */}
      <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
        <IconButton 
          onClick={() => navigate('/reports')}
          size="small"
          sx={{ mr: 1 }}
        >
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Daily Sales Report (DSR)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive daily sales analysis and insights
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          mb: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DatePicker
                label="From Date"
                value={filters.startDate}
                onChange={(newValue) => handleFilterChange('startDate', newValue)}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 0 } }
                  } 
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="To Date"
                value={filters.endDate}
                onChange={(newValue) => handleFilterChange('endDate', newValue)}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 0 } }
                  } 
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                startIcon={<AssessmentIcon />}
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Summary Cards */}
      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                borderLeft: '4px solid #4361ee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: '#4361ee15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <MoneyIcon sx={{ color: '#4361ee', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Sales
                  </Typography>
                  <Typography variant="h6" fontWeight="700">
                    SAR {totals.totalSales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                borderLeft: '4px solid #2e7d32',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: '#2e7d3215',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <ReceiptIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cash Sales
                  </Typography>
                  <Typography variant="h6" fontWeight="700">
                    SAR {totals.cashSales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                borderLeft: '4px solid #ed6c02',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: '#ed6c0215',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <TrendingUpIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Credit Sales
                  </Typography>
                  <Typography variant="h6" fontWeight="700">
                    SAR {totals.creditSales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0,
                borderLeft: '4px solid #d32f2f',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: '#d32f2f15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <VATIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    VAT Amount
                  </Typography>
                  <Typography variant="h6" fontWeight="700">
                    SAR {totals.totalVAT?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Data Grid */}
      <ReportDataGrid 
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={handleApplyFilters}
        exportFileName={`dsr-report-${new Date().toISOString().split('T')[0]}`}
        pageSize={25}
        height={700}
      />
    </Container>
  );
};

export default DSRReport;
